import { navigate } from 'gatsby';

/**
|--------------------------------------------------
| Auth redux reducers and action creators
|--------------------------------------------------
*/

/**
 * Initial state
 */
const initialState = {
  authenticated: false,
  authLoaded: false,
  authenticationError: null,
  user: {},
  signingOut: false,
  signOutError: null
};

/**
 * Authentication listener
 */
let unregisterAuthListener = null;

/**
|--------------------------------------------------
| Action types
|--------------------------------------------------
*/
/**
 * Signout requested action type
 */
export const SIGNOUT_REQUESTED = 'SIGNOUT_REQUESTED';
/**
 * Signout failed action type
 */
export const SIGNOUT_FAILED = 'SIGNOUT_FAILED';
/**
 * Authentication loaded
 */
export const AUTHENTICATION_LOADED = 'AUTHENTICATION_LOADED';
/**
 * Authentication failed action type
 */
export const AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED';
/**
 * User authenticated action type
 */
export const USER_AUTHENTICATED = 'USER_AUTHENTICATED';
/**
 * User updated action type
 */
export const USER_UPDATED = 'USER_UPDATED';
/**
 * User invalidated action type
 */
export const USER_INVALIDATED = 'USER_INVALIDATED';

/**
 * Auth redux reducer
 */
export default (state = initialState, action) => {
  switch (action.type) {
    case AUTHENTICATION_LOADED:
      return {
        ...state,
        authLoaded: true
      };
    case AUTHENTICATION_FAILED:
      return {
        ...state,
        authenticationError: action.error
      };
    case SIGNOUT_REQUESTED:
      return {
        ...state,
        signingOut: true,
        signOutError: null
      };
    case SIGNOUT_FAILED:
      return {
        ...state,
        signingOut: false,
        authenticationError: action.error
      };
    case USER_AUTHENTICATED:
      return {
        ...state,
        authenticationError: null,
        authenticated: true,
        user: action.user
      };
    case USER_UPDATED:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.user
        }
      };
    case USER_INVALIDATED:
      return {
        ...state,
        authenticated: false,
        signingOut: false,
        user: {}
      };
    default:
      return state;
  }
};

/**
|--------------------------------------------------
| Action creators
|--------------------------------------------------
*/

/**
 * Authentication failed action creator
 * @param {firebase.auth.Error} error Error object
 */
export const authenticationFailed = error => {
  return {
    type: AUTHENTICATION_FAILED,
    error
  };
};

/**
 * Sign out requsted action creator
 */
export const signOutRequested = () => ({
  type: SIGNOUT_REQUESTED
});

/**
 * Sign out failed action creator
 * @param {firebase.auth.Error} error Error object
 */
export const signOutFailed = error => {
  return {
    type: SIGNOUT_FAILED,
    error
  };
};

/**
 * Authentication loaded
 * Whether the code to check for authentication state has loaded
 */
export const authLoaded = () => ({
  type: AUTHENTICATION_LOADED
});

/**
 * Notify various monitoring services of login
 * @param {Object} user User firebase object
 */
function setUser(user) {
  window.Sentry.configureScope(scope => {
    scope.setUser({
      email: user.email,
      id: user.uid
    });
  });
}

/**
 * User authenticated action creator
 * @param {Object} user User profile
 */
export const userAuthenticated = user => {
  setUser(user);
  return {
    type: USER_AUTHENTICATED,
    user
  };
};

/**
 * User invalidated action creator
 * Used to purge user
 */
export const userInvalidated = () => ({
  type: USER_INVALIDATED
});

/**
 * User updated action creator
 * @param {Object} user User updates
 */
export const userUpdated = user => ({
  type: USER_UPDATED,
  user
});

/**
 * Start authenticating thunk
 * Listens for auth state change then dispatches user authentication actions
 */
export const startAuthenticating = () => (dispatch, getState, getFirebase) => {
  const firebase = getFirebase();
  unregisterAuthListener = firebase.auth().onAuthStateChanged(
    user => {
      if (user && user.email) {
        dispatch(userAuthenticated(user.toJSON()));
      }
      dispatch(authLoaded());
    },
    err => dispatch(authenticationFailed(err))
  );
};

/**
 * Notify various monitoring services of log out
 */
function purgeUser() {
  window.Sentry.configureScope(scope => {
    scope.setUser();
  });
}

/**
 * Destroys authentication listener
 */
export const stopAuthenticating = () => {
  if (unregisterAuthListener) {
    unregisterAuthListener();
    unregisterAuthListener = null;
  }
};

/**
 * Sign out thunk
 * Requests sign out then dispatches relevant actions based on result
 */
export const signOut = redirectUrl => (dispatch, getState, getFirebase) => {
  dispatch(signOutRequested());
  getFirebase()
    .logout()
    .then(() => {
      purgeUser();
      window.localStorage.clear();
      dispatch(userInvalidated());
      navigate(redirectUrl || '/');
    })
    .catch(err => dispatch(signOutFailed(err)));
};

function ensureUserIsSet(auth) {
  return new Promise(function(resolve) {
    (function waitForUser() {
      if (auth.currentUser) return resolve();
      setTimeout(waitForUser, 30);
    })();
  });
}

/**
 * Change email thunk
 * Change the email of the user. Requires a recent login
 */
export const changeEmail = email => (dispatch, getState, getFirebase) => {
  const lowerCaseEmail = email.toLowerCase();
  const state = getState();
  const firebase = getFirebase();
  const auth = firebase.auth();
  return (
    ensureUserIsSet(auth)
      // Update email
      .then(() => auth.currentUser.updateEmail(lowerCaseEmail))
      // Record in database
      .then(() =>
        firebase.set(`/users/${state.auth.user.uid}/email`, lowerCaseEmail)
      )
      .then(() => {
        dispatch(userUpdated({ email: lowerCaseEmail }));
      })
  );
};

/**
 * Change display name thunk
 * Change the display name of the user.
 */
export const changeDisplayName = displayName => (
  dispatch,
  getState,
  getFirebase
) => {
  const firebase = getFirebase();
  firebase
    .set(`/users/${firebase.auth().currentUser.uid}/displayName`, displayName)
    .then(() => {
      dispatch(userUpdated({ displayName }));
    });
};
